import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs';
import { ApplicationControllerService } from './cc-api/services/application-controller.service';
import { UserService } from './services/user.service';
import { SimpleOauth2User } from './cc-api/models';

@Injectable({
  providedIn: 'root',
})
export class PageAccessGuard implements CanActivate {
  private roles: Set<string> = new Set();
  private allowedEnvironments = [];
  private user: SimpleOauth2User;

  constructor(
    private applicationControllerService: ApplicationControllerService,
    private router: Router,
    private userService: UserService,
  ) {}

  /**
   * Checks if the user has access to the requested page and handles redirection if denied.
   * @param route - The ActivatedRouteSnapshot representing the requested route.
   * @param state - The RouterStateSnapshot representing the router state.
   * @returns A boolean indicating if the user has access to the page.
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> | boolean {
    //If user is already loaded then get user data from user service
    if (this.userService.userLoaded) {
      this.setUser(this.userService.user);
      const access = this.isPageAccessible(route);
      if (!access) {
        this.navigateToNoAccess();
      }
      return access;
    }
    // else fetch user data using me call
    return this.applicationControllerService.meUsingGET().pipe(
      map((res: SimpleOauth2User) => {
        this.setUser(res);
        return this.isPageAccessible(route);
      }),
      catchError(() => of(false)),
      tap((access: boolean) => {
        if (!access) {
          this.navigateToNoAccess();
        }
      }),
    );
  }

  setUser(user) {
    this.user = user;
    this.allowedEnvironments = this.user?.allowedClusterIds;
    this.user?.authorities?.forEach((element) => {
      this.roles.add(element?.authority);
    });
  }

  /**
   * Traverses the route tree to find the target URL and returns the roles to check.
   * @param root The root ActivatedRouteSnapshot of the current route.
   * @returns An object containing the neededRoles array and checkForSome flag, or undefined if not found.
   */
  getRolesToCheckFor(
    root: ActivatedRouteSnapshot,
  ): { neededRoles: string[]; checkForSome: boolean } | undefined {
    while (root) {
      if (root?.children && root?.children?.length) {
        root = root?.children[0];
      } else if (root?.data && root?.data?.title && root?.params) {
        return {
          neededRoles: root?.data?.authFor || [],
          checkForSome: root?.data?.checkForSome || false,
        };
      } else {
        return;
      }
    }
  }

  /**
   * Checks if the user has at least one of the required roles.
   * @param neededRoles An array of required roles.
   * @returns `true` if the user has at least one of the required roles, `false` otherwise.
   */
  hasSomeRequiredRoles(neededRoles: string[]) {
    for (const role of neededRoles) {
      if (this.roles.has(role)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Checks if the user has all the required roles.
   * @param neededRoles An array of required roles.
   * @returns `true` if the user has all the required roles, `false` otherwise.
   */
  hasAllRequiredRoles(neededRoles: string[]) {
    for (const role of neededRoles) {
      if (!this.roles.has(role)) {
        return false;
      }
    }
    return true;
  }

  isClusterAllowed(clusterId) {
    return (
      this.allowedEnvironments.filter((envId) => envId === clusterId).length > 0
    );
  }

  /**
   * Determines whether the user meets the required roles for a given set of roles.
   * @param neededRoles The roles to check against.
   * @param checkForSome A boolean indicating whether to check for some or all roles.
   * @returns A boolean indicating whether the user meets the role requirements.
   */
  private meetsRequiredRoles(
    neededRoles: string[],
    checkForSome: boolean,
  ): boolean {
    if (neededRoles?.length > 0) {
      return checkForSome
        ? this.hasSomeRequiredRoles(neededRoles)
        : this.hasAllRequiredRoles(neededRoles);
    }
    return true;
  }

  /**
   * Determines whether a specific page is accessible based on user roles and permissions.
   * @param route The route snapshot representing the current route.
   * @returns A boolean indicating whether the page is accessible.
   */
  isPageAccessible(route: ActivatedRouteSnapshot): boolean {
    const clusterId = route?.params?.clusterId;
    const rolesToCheck = this.getRolesToCheckFor(route);

    // If the route is pointing to an environment level page, then check if the user can access the environment.
    // For all other routes, check page access based on roles specified in authFor.
    if (this.allowedEnvironments?.length && clusterId) {
      return this.isClusterAllowed(clusterId);
    } else {
      return this.meetsRequiredRoles(
        rolesToCheck?.neededRoles || [],
        rolesToCheck.checkForSome,
      );
    }
  }

  private navigateToNoAccess(): void {
    this.router.navigate(['/no-access']);
  }
}
